(() => {
  const debounce = (fn, ms) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  };

  const formatMoney = (cents, format) => {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney && format) {
      return Shopify.formatMoney(cents, format);
    }
    return (Number(cents) / 100).toFixed(2);
  };

  /** Predictive Search JSON returns major currency units (e.g. 180.00), not cents. */
  const predictivePriceToCents = (price) => {
    if (price == null || price === '') return null;
    const amount = Number(String(price).replace(',', '.'));
    if (Number.isNaN(amount)) return null;
    return Math.round(amount * 100);
  };

  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;');

  const renderResults = (root, results, query) => {
    const moneyFormat = root.dataset.moneyFormat;
    const panel = root.querySelector('[data-babaa-predictive-panel]');
    const noResults = root.dataset.noResults || 'No results';
    const viewAll = root.dataset.viewAll || 'View all results';
    const searchUrl = root.dataset.searchUrl || '/search';

    if (!panel) return;

    const products = results?.products || [];
    const queries = results?.queries || [];
    const productSearchQuery = `type=product`;

    const items = [];

    queries.slice(0, 3).forEach((entry) => {
      const label = entry.text || entry.styled_text || query;
      const url = entry.url || `${searchUrl}?q=${encodeURIComponent(label)}&${productSearchQuery}`;
      items.push(`<li class="babaa-predictive-search__item babaa-predictive-search__item--query">
        <a class="babaa-predictive-search__link babaa-predictive-search__link--text" href="${url}">
          <span class="babaa-predictive-search__title">${escapeHtml(label)}</span>
        </a>
      </li>`);
    });

    products.forEach((product) => {
      const image = product.featured_image?.url || product.image || '';
      const price = product.price_min ?? product.price;
      const priceCents = predictivePriceToCents(price);
      const priceHtml =
        priceCents != null
          ? `<span class="babaa-predictive-search__price">${formatMoney(priceCents, moneyFormat)}</span>`
          : '';

      items.push(`<li class="babaa-predictive-search__item">
        <a class="babaa-predictive-search__link" href="${product.url}">
          ${
            image
              ? `<img class="babaa-predictive-search__thumb" src="${image}" alt="" width="48" height="60" loading="lazy">`
              : ''
          }
          <span class="babaa-predictive-search__meta">
            <span class="babaa-predictive-search__title">${escapeHtml(product.title)}</span>
            ${priceHtml}
          </span>
        </a>
      </li>`);
    });

    if (!items.length) {
      panel.innerHTML = `<p class="babaa-predictive-search__empty">${noResults}</p>`;
      panel.hidden = false;
      return;
    }

    const viewAllUrl = `${searchUrl}?q=${encodeURIComponent(query)}&${productSearchQuery}`;
    panel.innerHTML = `<ul class="babaa-predictive-search__list" role="listbox">${items.join('')}</ul>
      <a class="babaa-predictive-search__view-all" href="${viewAllUrl}">${viewAll}</a>`;
    panel.hidden = false;
  };

  const closePanel = (root) => {
    const panel = root.querySelector('[data-babaa-predictive-panel]');
    const input = root.querySelector('[data-babaa-predictive-input]');
    if (panel) {
      panel.hidden = true;
      panel.innerHTML = '';
    }
    input?.setAttribute('aria-expanded', 'false');
  };

  const initPredictiveSearch = (root) => {
    if (root.dataset.predictiveInitialized) return;
    root.dataset.predictiveInitialized = 'true';

    const form = root.querySelector('[data-babaa-predictive-form]');
    const input = root.querySelector('[data-babaa-predictive-input]');
    const toggle = root.querySelector('[data-babaa-search-toggle]');
    const field = root.querySelector('[data-babaa-search-field]');
    const predictiveUrl = root.dataset.predictiveUrl;
    if (!form || !input || !predictiveUrl || !toggle || !field) return;

    let activeController = null;

    const openSearch = () => {
      root.classList.add('is-open');
      field.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      input.tabIndex = 0;
      requestAnimationFrame(() => input.focus());
    };

    const closeSearch = () => {
      root.classList.remove('is-open');
      field.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      input.tabIndex = -1;
      closePanel(root);
    };

    const fetchSuggestions = debounce(async () => {
      const query = input.value.trim();
      if (query.length < 2) {
        closePanel(root);
        return;
      }

      activeController?.abort();
      activeController = new AbortController();

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'hide',
      });

      try {
        const response = await fetch(`${predictiveUrl}.json?${params}`, {
          signal: activeController.signal,
        });
        if (!response.ok) throw new Error('Predictive search failed');
        const payload = await response.json();
        renderResults(root, payload?.resources?.results || {}, query);
        input.setAttribute('aria-expanded', 'true');
      } catch (error) {
        if (error.name !== 'AbortError') closePanel(root);
      }
    }, 220);

    toggle.addEventListener('click', () => {
      if (root.classList.contains('is-open')) closeSearch();
      else openSearch();
    });

    input.addEventListener('input', fetchSuggestions);

    form.addEventListener('submit', (event) => {
      if (!input.value.trim()) {
        event.preventDefault();
        openSearch();
        input.focus();
        return;
      }
      closePanel(root);
    });

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) closeSearch();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeSearch();
    });

  };

  const boot = () => {
    document.querySelectorAll('[data-babaa-predictive-search]').forEach(initPredictiveSearch);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target?.querySelectorAll?.('[data-babaa-predictive-search]')?.forEach(initPredictiveSearch);
  });
})();
